import type { FC, ReactNode } from 'react'

import {
    createContext,
    useCallback,
    useContext,
    useRef,
    useState
} from 'react'
import { Button } from '@/components/kit/button'
import { Sheet } from '@/components/kit/sheet'

export type ConfirmOptions = {
    title: string
    description?: string
    confirmLabel?: string
    cancelLabel?: string
    tone?: 'primary' | 'danger'
}

const ConfirmContext = createContext<(options: ConfirmOptions) => Promise<boolean>>(async () => false)

export const ConfirmProvider: FC<{ children: ReactNode }> = ({ children }): ReactNode => {
    const [options, setOptions] = useState<ConfirmOptions | null>(null)
    const [open, setOpen] = useState(false)
    const resolverRef = useRef<((value: boolean) => void) | null>(null)

    const confirm = useCallback((next: ConfirmOptions) => new Promise<boolean>(resolve => {
        resolverRef.current?.(false)
        resolverRef.current = resolve
        setOptions(next)
        setOpen(true)
    }), [])

    const settle = (value: boolean) => {
        resolverRef.current?.(value)
        resolverRef.current = null
        setOpen(false)
    }

    return (
        <ConfirmContext.Provider value={confirm}>
            {children}

            <Sheet
                open={open}
                onClose={() => settle(false)}
                title={options?.title ?? ''}
                description={options?.description}
                size='sm'
                footer={(
                    <div className='grid grid-cols-2 gap-3'>
                        <Button variant='secondary' size='lg' onClick={() => settle(false)}>
                            {options?.cancelLabel ?? 'Cancel'}
                        </Button>

                        <Button
                            variant={options?.tone === 'danger' ? 'danger' : 'primary'}
                            size='lg'
                            onClick={() => settle(true)}
                        >
                            {options?.confirmLabel ?? 'Confirm'}
                        </Button>
                    </div>
                )}
            />
        </ConfirmContext.Provider>
    )
}

export const useConfirm = () => useContext(ConfirmContext)
